import { type Product } from '@deizi/shared';
import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { Icon } from '../../components/Icon';
import { ErrorBox, Skeleton } from '../../components/States';
import { ApiClientError } from '../../lib/api';
import { useProduct } from '../../lib/products-api';
import { useIsAdmin } from '../../lib/session';
import { useIsPhone } from '../../lib/use-media';
import { ArchiveDialog, useProductActions } from './actions';
import { Cover, StatusBadge } from './common';
import { FilesTab } from './FilesTab';
import { ListingsTab } from './ListingsTab';
import { MediaTab } from './MediaTab';
import { PricesTab } from './PricesTab';
import { StatsTab } from './StatsTab';
import { OverviewTab } from './OverviewTab';
import { VariantsTab } from './VariantsTab';

export const PRODUCT_TABS = [
  ['visao', 'Visão geral'],
  ['variacoes', 'Variações'],
  ['midia', 'Mídia'],
  ['arquivos', 'Arquivos'],
  ['precos', 'Preços'],
  ['anuncios', 'Anúncios'],
  ['estatisticas', 'Estatísticas'],
] as const;
export type ProductTab = (typeof PRODUCT_TABS)[number][0];

function tabLabel(tab: ProductTab, label: string, p: Product): string {
  if (tab === 'variacoes') return `${label} · ${p.variants.length}`;
  if (tab === 'midia') return `${label} · ${p.counts.media}`;
  if (tab === 'anuncios') return `${label} · ${p.counts.listings}`;
  if (tab === 'arquivos' && p.counts.files) return `${label} · ${p.counts.files}`;
  return label;
}

export function ProductPage() {
  const { id = '', tab: tabParam } = useParams();
  const tab: ProductTab = PRODUCT_TABS.find(([k]) => k === tabParam)?.[0] ?? 'visao';
  const product = useProduct(id);
  const phone = useIsPhone();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();
  const [archiving, setArchiving] = useState(false);
  const actions = useProductActions((p) => void navigate(`/produtos/${p.id}`));

  if (product.error && !product.data) {
    const missing = product.error instanceof ApiClientError && product.error.status === 404;
    return (
      <div className="card">
        <ErrorBox
          title={missing ? 'Produto não encontrado' : 'Não foi possível carregar o produto'}
          onRetry={() => void product.refetch()}
        />
      </div>
    );
  }
  const p = product.data;
  if (!p) return <Skeleton rows={2} height={200} />;

  const archived = p.status === 'archived';
  return (
    <>
      <div className="pagehead">
        <div className="row" style={{ gap: 14, alignItems: 'flex-start', minWidth: 0 }}>
          {phone ? null : (
            <div style={{ width: 72, flex: '0 0 72px' }}>
              <Cover url={p.coverUrl} height={72} label="capa" />
            </div>
          )}
          <div style={{ minWidth: 0 }}>
            <div className="crumb">
              <Link to="/produtos">Produtos</Link> / {p.sku}
            </div>
            <div className="row wrap" style={{ gap: 10 }}>
              <h1>{p.name}</h1>
              <StatusBadge status={p.status} custom={p.custom} />
            </div>
            <p className="sm muted">
              {[
                p.origin === 'own' ? 'Projeto próprio' : 'Projeto de terceiro',
                p.category,
                p.materials.join(', ') || null,
                p.custom && p.customer ? `para ${p.customer.name}` : null,
              ]
                .filter(Boolean)
                .join(' · ')}
            </p>
          </div>
        </div>
        <div className="row wrap" style={{ gap: 10 }}>
          <button
            type="button"
            className="btn"
            onClick={() => actions.duplicate.mutate(p.id)}
            disabled={actions.duplicate.isPending}
          >
            Duplicar
          </button>
          <Link className="btn" to={`/orcamento-rapido?produto=${p.id}`}>
            Orçar com este produto
          </Link>
          {isAdmin && !phone ? (
            <a className="btn g" href={`/api/v1/products/${p.id}/bundle.zip`} download>
              Exportar (.zip)
            </a>
          ) : null}
          {archived ? (
            <button type="button" className="btn" onClick={() => actions.unarchive.mutate(p.id)}>
              Desarquivar
            </button>
          ) : (
            <button type="button" className="btn d" onClick={() => setArchiving(true)}>
              Arquivar
            </button>
          )}
        </div>
      </div>
      {archived ? (
        <div className="alert warn">
          <Icon name="alert" />
          <div>
            <b>Produto arquivado.</b> Não aparece no catálogo nem no Orçamento rápido. Anúncios
            ativos não foram pausados nos canais.
          </div>
        </div>
      ) : null}
      {!p.canSell ? (
        <div className="alert err">
          <Icon name="alert" />
          <div>
            <b>Projeto de terceiro sem licença comercial.</b> Fica bloqueado para venda até você
            registrar uma licença que permita vender (Visão geral › Origem e licença).
          </div>
        </div>
      ) : null}
      <nav className="tabs" aria-label="Seções do produto">
        {PRODUCT_TABS.map(([k, label]) => (
          <Link
            key={k}
            to={`/produtos/${p.id}${k === 'visao' ? '' : `/${k}`}`}
            className={tab === k ? 'on' : ''}
            aria-current={tab === k ? 'page' : undefined}
          >
            {tabLabel(k, label, p)}
          </Link>
        ))}
      </nav>
      {tab === 'visao' ? <OverviewTab product={p} /> : null}
      {tab === 'variacoes' ? <VariantsTab product={p} /> : null}
      {tab === 'midia' ? <MediaTab product={p} /> : null}
      {tab === 'arquivos' ? <FilesTab product={p} /> : null}
      {tab === 'precos' ? <PricesTab product={p} /> : null}
      {tab === 'anuncios' ? <ListingsTab product={p} /> : null}
      {tab === 'estatisticas' ? <StatsTab product={p} /> : null}
      {archiving ? (
        <ArchiveDialog
          name={p.name}
          listings={p.counts.listings}
          onClose={() => setArchiving(false)}
          onConfirm={() => {
            actions.archive.mutate(p.id);
            setArchiving(false);
          }}
        />
      ) : null}
    </>
  );
}
